/**
 * Admin-Only Access Wrapper
 * 
 * Restricts access to sensitive components like the System Health Dashboard
 * to authenticated users with admin privileges
 */

import { Alert, Container, Paper, Stack, Text, Title } from '@mantine/core';
import { IconShield, IconX } from '@tabler/icons-react';
import React from 'react';

interface AdminOnlyWrapperProps {
    children: React.ReactNode;
    requiredRole?: string;
    fallbackMessage?: string;
}

interface UserContext {
    isAuthenticated: boolean;
    hasAdminAccess: boolean;
    roles: string[];
    tenant: string;
}

// Mock user context - in production this would come from your auth provider
function useUserContext(): UserContext {
    const authToken = sessionStorage.getItem('auth:token');
    const userRoles = JSON.parse(sessionStorage.getItem('user:roles') || '["user"]');
    const tenant = sessionStorage.getItem('tenant') || 'default';
    
    // Check for admin indicators
    const hasAdminAccess = !!(
        userRoles.includes('admin') ||
        userRoles.includes('system-admin') ||
        userRoles.includes('super-user') ||
        // Development override - remove in production
        localStorage.getItem('dev:admin-override') === 'true'
    );

    return {
        isAuthenticated: !!authToken,
        hasAdminAccess,
        roles: userRoles,
        tenant
    };
}

export const AdminOnlyWrapper: React.FC<AdminOnlyWrapperProps> = ({ 
    children, 
    requiredRole = 'admin',
    fallbackMessage 
}) => {
    const user = useUserContext();

    // Check authentication
    if (!user.isAuthenticated) {
        return (
            <Container size="sm" py="xl">
                <Paper p="xl" withBorder>
                    <Stack gap="md" ta="center">
                        <IconShield size={48} color="var(--mantine-color-red-6)" />
                        <Title order={2}>Authentication Required</Title>
                        <Text c="dimmed">
                            You must be authenticated to access this resource.
                        </Text>
                        <Alert color="yellow" variant="light">
                            Please log in and ensure you have the necessary permissions.
                        </Alert>
                    </Stack>
                </Paper>
            </Container>
        );
    }

    // Check admin access
    if (!user.hasAdminAccess) {
        return (
            <Container size="sm" py="xl">
                <Paper p="xl" withBorder>
                    <Stack gap="md" ta="center">
                        <IconX size={48} color="var(--mantine-color-red-6)" />
                        <Title order={2}>Access Denied</Title>
                        <Text c="dimmed">
                            {fallbackMessage || `This resource requires ${requiredRole} privileges.`}
                        </Text>
                        <Alert color="red" variant="light">
                            <Stack gap="xs">
                                <Text fw={500}>Current User Access:</Text>
                                <Text size="sm">Tenant: {user.tenant}</Text>
                                <Text size="sm">Roles: {user.roles.join(', ')}</Text>
                                <Text size="sm">Admin Access: {user.hasAdminAccess ? 'Yes' : 'No'}</Text>
                            </Stack>
                        </Alert>
                        {process.env.NODE_ENV === 'development' && (
                            <Alert color="blue" variant="light">
                                <Stack gap="xs">
                                    <Text fw={500}>Development Override:</Text>
                                    <Text size="sm">
                                        Run the following in browser console to enable admin access:
                                    </Text>
                                    <Text size="xs" ff="mono">
                                        localStorage.setItem(&apos;dev:admin-override&apos;, &apos;true&apos;); location.reload();
                                    </Text>
                                </Stack>
                            </Alert>
                        )}
                    </Stack>
                </Paper>
            </Container>
        );
    }

    // User has access - render the protected component
    return <>{children}</>;
};

// Helper hook for checking admin access in components
export function useIsAdmin(): boolean {
    const user = useUserContext();
    return user.isAuthenticated && user.hasAdminAccess;
}

// Helper hook for getting current user context
export function useCurrentUser(): UserContext {
    return useUserContext();
}

export default AdminOnlyWrapper;